export type BodegaRecogidaSeleccion = '' | '95' | '93' | '94' | 'ecommerce' | '97' | '98'
export type RecogeRespuesta = '' | 'si' | 'no'

export interface BodegaRecogidaLeida {
  idMarket: BodegaRecogidaSeleccion
  recogeRespuesta: RecogeRespuesta
}

const BODEGAS_VALIDAS: readonly BodegaRecogidaSeleccion[] = ['95', '93', '94', 'ecommerce', '97', '98']

interface MetaRow {
  key?: unknown
  value?: unknown
}

function asText(value: unknown): string | undefined {
  if (value == null) return undefined
  if (typeof value === 'object') return undefined
  const text = String(value).trim()
  if (!text || text === 'null' || text === 'undefined') return undefined
  return text
}

function rowValue(row: unknown, key: string): string | undefined {
  if (!row || typeof row !== 'object') return undefined
  const item = row as MetaRow
  if (String(item.key ?? '') !== key) return undefined
  return asText(item.value)
}

/** Lee una clave en meta_data, sea arreglo `{key,value}`, objeto o ambos mezclados. */
export function readOrderMetaValue(source: unknown, key: string): string | undefined {
  if (Array.isArray(source)) {
    for (const item of source) {
      const text = rowValue(item, key)
      if (text) return text
    }
    return undefined
  }
  if (!source || typeof source !== 'object') return undefined

  const record = source as Record<string, unknown>
  const direct = asText(record[key])
  if (direct) return direct

  const nested = readOrderMetaValue(record.meta_data, key)
  if (nested) return nested

  for (const value of Object.values(record)) {
    const text = rowValue(value, key)
    if (text) return text
  }
  return undefined
}

export function normalizeBodegaId(value: unknown): BodegaRecogidaSeleccion {
  const text = asText(value)
  if (!text) return ''
  if (text === '96') return '97'
  if (BODEGAS_VALIDAS.includes(text as BodegaRecogidaSeleccion)) {
    return text as BodegaRecogidaSeleccion
  }
  return ''
}

/** null = no vino en el pedido. 0 y "no" son una respuesta real. */
export function parseRecogeFlag(value: unknown): boolean | null {
  if (value == null || value === '') return null
  if (value === true || value === 1) return true
  if (value === false || value === 0) return false
  const text = String(value).trim().toLowerCase()
  if (text === '1' || text === 'true' || text === 'si' || text === 'sí' || text === 'yes') return true
  if (text === '0' || text === 'false' || text === 'no') return false
  return null
}

function firstBodega(values: unknown[]): BodegaRecogidaSeleccion {
  for (const value of values) {
    const bodega = normalizeBodegaId(value)
    if (bodega) return bodega
  }
  return ''
}

function firstRecoge(values: unknown[]): boolean | null {
  for (const value of values) {
    const parsed = parseRecogeFlag(value)
    if (parsed !== null) return parsed
  }
  return null
}

function extraRecord(order: Record<string, unknown>): Record<string, unknown> {
  const extra = order.extraDetails
  if (!extra || typeof extra !== 'object' || Array.isArray(extra)) return {}
  return extra as Record<string, unknown>
}

/**
 * Valor guardado del pedido.
 * Busca en extraDetails, en la raíz y en meta_data (arreglo u objeto).
 * Un vacío en un canal no tapa el valor que sí llegó por el otro.
 */
export function readBodegaRecogida(order: unknown): BodegaRecogidaLeida {
  if (!order || typeof order !== 'object') {
    return { idMarket: '', recogeRespuesta: '' }
  }
  const record = order as Record<string, unknown>
  const extra = extraRecord(record)
  const meta = record.meta_data

  let idMarket = firstBodega([
    extra.id_market,
    extra.bodega_recogida_id,
    record.id_market,
    record.bodega_recogida_id,
    readOrderMetaValue(meta, 'id_market'),
    readOrderMetaValue(meta, 'bodega_recogida_id'),
    readOrderMetaValue(record, 'id_market'),
    readOrderMetaValue(record, 'bodega_recogida_id')
  ])

  const recoge = firstRecoge([
    extra.recoge_en_bodega,
    record.recoge_en_bodega,
    readOrderMetaValue(meta, 'recoge_en_bodega'),
    readOrderMetaValue(record, 'recoge_en_bodega')
  ])

  if (!idMarket && recoge === true) idMarket = 'ecommerce'

  return {
    idMarket,
    recogeRespuesta: recoge === true ? 'si' : recoge === false ? 'no' : ''
  }
}

export interface BodegaRecogidaInput {
  idMarket?: string
  recogeRespuesta?: string
}

/**
 * Lo que se debe enviar.
 * El select gana si tiene valor. Si está vacío, se conserva lo que ya traía el pedido.
 */
export function resolveBodegaRecogidaForSubmit(
  order: unknown,
  input?: BodegaRecogidaInput
): BodegaRecogidaLeida {
  const stored = readBodegaRecogida(order)
  const inputMarket = normalizeBodegaId(input?.idMarket)
  const inputRecoge = input?.recogeRespuesta === 'si' || input?.recogeRespuesta === 'no'
    ? input.recogeRespuesta
    : ''

  return {
    idMarket: inputMarket || stored.idMarket,
    recogeRespuesta: inputRecoge || stored.recogeRespuesta
  }
}

export function recogeFlagFromRespuesta(recogeRespuesta: RecogeRespuesta): 0 | 1 | null {
  if (recogeRespuesta === 'si') return 1
  if (recogeRespuesta === 'no') return 0
  return null
}
